import * as React from "react";
import { useMergeRefs } from "@floating-ui/react";
import { AnalyticsTrigger } from "../../helpers/analytics/analytics-trigger/analytics-trigger.mjs";
import { ClearButton } from "../clear-button/clear-button.mjs";
import { FormHelper } from "../form-helper/form-helper.mjs";
import { Icon } from "../../icon/icon/icon.mjs";
import { Image } from "../../image/image.mjs";
import { PropsChecker } from "../../helpers/props-checker/props-checker.mjs";
const TextField = React.forwardRef(function TextField2(props, ref) {
  const innerRef = useMergeRefs([props.inputRef, ref]);
  return /* @__PURE__ */ React.createElement(
    PropsChecker,
    {
      componentName: "TextField",
      componentProps: props,
      defaultValueName: "defaultValue",
      valueName: "value",
      onChangeNames: "onChange",
      isComponentDisabled: props.isDisabled || props.isReadOnly
    },
    /* @__PURE__ */ React.createElement(
      PropsChecker,
      {
        componentName: "TextField",
        componentProps: props,
        defaultValueName: "defaultIsFocused",
        valueName: "isFocused",
        onChangeNames: ["onFocus", "onBlur"]
      },
      /* @__PURE__ */ React.createElement(
        AnalyticsTrigger,
        {
          trigger: [
            {
              callback: "onFocus",
              id: "Field - Focus - ".concat(props.id),
              elementId: "".concat(props.id),
              elementType: "field",
              interactionType: "focus"
            },
            {
              callback: "onManualChange",
              id: "Field - Filled - ".concat(props.id),
              elementId: "".concat(props.id),
              elementType: "field",
              interactionType: "fill"
            },
            {
              callback: "onClear",
              id: "Field - Clear - ".concat(props.id),
              elementId: "".concat(props.id),
              elementType: "field",
              interactionType: "clear"
            }
          ]
        },
        /* @__PURE__ */ React.createElement(TextFieldInner, { ...props, inputRef: innerRef })
      )
    )
  );
});
const _TextFieldInner = class _TextFieldInner extends React.Component {
  constructor(props) {
    var _a;
    super(props);
    this.input = null;
    this.clearButton = React.createRef();
    this.onChange = (event) => {
      const value = event.currentTarget.value;
      if (typeof this.props.value === "undefined") {
        this.setState({
          value
        });
      }
      if (this.props.onChange) {
        this.props.onChange(value);
      }
      if (this.props.onManualChange && !this.state.isChanged) {
        this.props.onManualChange();
      }
      this.setState({
        isChanged: true
      });
    };
    this.onFocus = () => {
      const done = () => {
        if (this.props.onFocus) {
          this.props.onFocus();
        }
      };
      if (this.props.isReadOnly || this.props.isStatic) {
        return done();
      }
      if (typeof this.props.isFocused === "undefined") {
        this.setState({
          isFocused: true
        });
      }
      done();
    };
    this.onBlur = (event) => {
      var _a2;
      const clickedOnCloseButton = (event == null ? void 0 : event.relatedTarget) instanceof Element && ((_a2 = this.clearButton.current) == null ? void 0 : _a2.contains(event.relatedTarget));
      if (clickedOnCloseButton) {
        this.setState({
          isFocused: false
        });
        return;
      }
      if (typeof this.props.isFocused === "undefined") {
        this.setState({
          isFocused: false
        });
      }
      if (this.props.onBlur) {
        this.props.onBlur(event);
      }
    };
    this.clear = (event) => {
      event.preventDefault();
      const value = "";
      const prevValue = this.value;
      if (typeof this.props.value === "undefined") {
        this.setState({
          value
        });
      }
      if (this.props.onChange) {
        this.props.onChange(value);
      }
      if (this.props.onClear) {
        this.props.onClear(event);
      }
      if (this.input && prevValue) {
        this.input.focus();
      }
    };
    this.setInputRef = (element) => {
      this.input = element;
      if (element && this.props.inputRef) {
        this.props.inputRef(element);
      }
    };
    this.getInner = () => {
      const {
        id,
        label,
        className,
        employee,
        focusLabel,
        helpMessage,
        errorMessage,
        successMessage,
        employeeMessage,
        hideLabel,
        onChange,
        onKeyDown,
        onFocus,
        onBlur,
        onClear,
        icon,
        input,
        isDisabled,
        isInvalid,
        isReadOnly,
        isStatic,
        defaultIsFocused,
        isFocused,
        size,
        align,
        defaultValue,
        value,
        isTextarea,
        inputRef,
        inputStyle,
        inputClass,
        type,
        contentRenderer,
        isClearable,
        image,
        placeholderLabel,
        "aria-describedby": ariaDescribedBy,
        clearButtonLabel,
        required,
        onManualChange,
        ...rest
      } = this.props;
      const InputElement = this.props.isTextarea ? "textarea" : "input";
      return /* @__PURE__ */ React.createElement(React.Fragment, null, this.getIcon(), this.shouldRenderImage(this.props.image) && /* @__PURE__ */ React.createElement(Image, { ...this.props.image, className: "textfield__image" }), /* @__PURE__ */ React.createElement(
        InputElement,
        {
          ...rest,
          ...this.props.input,
          id: this.props.id,
          readOnly: this.props.isReadOnly || this.props.isStatic,
          className: this.inputBEM(),
          onChange: this.onChange,
          onFocus: this.onFocus,
          onBlur: this.onBlur,
          onKeyDown: this.props.onKeyDown,
          value: this.value,
          disabled: this.props.isDisabled,
          placeholder: this.getPlaceholder(),
          ref: this.setInputRef,
          style: this.props.inputStyle,
          type: !this.props.isTextarea ? this.props.type : void 0,
          "aria-invalid": this.isInvalid,
          "aria-describedby": this.getAriaDescribedBy(),
          "aria-required": this.props.required
        }
      ), this.props.isClearable && this.getClearButton());
    };
    this.state = {
      isFocused: props.defaultIsFocused || false,
      value: (_a = props.defaultValue) != null ? _a : "",
      isChanged: false
    };
  }
  get value() {
    if (this.props.isReadOnly || this.props.isDisabled) {
      return typeof this.props.value !== "undefined" ? this.props.value : this.state.value;
    }
    return this.props.onChange && typeof this.props.value !== "undefined" ? this.props.value : this.state.value;
  }
  get isFocused() {
    if (this.props.isReadOnly || this.props.isDisabled) {
      return typeof this.props.isFocused !== "undefined" ? this.props.isFocused : this.state.isFocused;
    }
    return this.props.onBlur && this.props.onFocus && typeof this.props.isFocused !== "undefined" ? this.props.isFocused : this.state.isFocused;
  }
  get isInvalid() {
    return this.props.isInvalid || !!this.props.errorMessage;
  }
  BEM() {
    const classArray = ["textfield"];
    if (this.value) {
      classArray.push("is-dirty");
    }
    if (this.props.isDisabled) {
      classArray.push("is-disabled");
    }
    if (this.isInvalid) {
      classArray.push("is-invalid");
    }
    if (this.props.isStatic) {
      classArray.push("is-static");
    }
    if (this.isFocused) {
      classArray.push("is-focused");
    }
    if (this.props.size) {
      classArray.push("textfield--" + this.props.size);
    }
    if (this.props.align) {
      classArray.push("textfield--" + this.props.align);
    }
    if (this.props.hideLabel) {
      classArray.push("textfield--no-label-" + this.props.hideLabel);
    }
    if (this.props.icon || this.props.isClearable) {
      classArray.push("textfield--with-icon");
    }
    if (this.props.employee) {
      classArray.push("textfield--employee");
    }
    if (this.shouldRenderImage(this.props.image)) {
      classArray.push("textfield--image");
    }
    if (this.props.placeholderLabel && this.props.hideLabel !== "no-break") {
      classArray.push("textfield--no-label-no-break");
    }
    if (this.props.className) {
      classArray.push(this.props.className);
    }
    return classArray.join(" ");
  }
  inputBEM() {
    const classArray = ["textfield__input"];
    if (this.props.isTextarea) {
      classArray.push("textfield__textarea-input");
    }
    if (this.props.inputClass) {
      classArray.push(this.props.inputClass);
    }
    return classArray.join(" ");
  }
  getIcon() {
    if (this.props.icon) {
      return /* @__PURE__ */ React.createElement(Icon, { modifiers: this.props.icon, className: "textfield__icon" });
    } else {
      return null;
    }
  }
  getLabel() {
    if (this.isFocused && this.props.focusLabel) {
      return this.props.focusLabel;
    }
    return this.props.label;
  }
  getClearButton() {
    if (this.props.isDisabled || this.props.isReadOnly) {
      return null;
    }
    if (this.value && this.value.length > 0 || this.props.isClearable === "always") {
      return /* @__PURE__ */ React.createElement(ClearButton, { className: "textfield__clear-btn", onClick: this.clear, label: this.props.clearButtonLabel, ref: this.clearButton, tabIndex: -1 });
    }
    return null;
  }
  shouldRenderImage(image) {
    return !!image && this.value !== "";
  }
  getPlaceholder() {
    if (this.props.placeholderLabel) {
      return this.props.label;
    }
  }
  getAriaDescribedBy() {
    const arr = [];
    if (this.props["aria-describedby"]) {
      arr.push(this.props["aria-describedby"]);
    }
    if (this.props.helpMessage) {
      arr.push("".concat(this.props.id, "-help-message"));
    }
    if (this.props.errorMessage) {
      arr.push("".concat(this.props.id, "-error-message"));
    }
    if (this.props.successMessage) {
      arr.push("".concat(this.props.id, "-success-message"));
    }
    if (this.props.employeeMessage) {
      arr.push("".concat(this.props.id, "-employee-message"));
    }
    return arr.join(" ");
  }
  render() {
    return /* @__PURE__ */ React.createElement("div", { className: this.BEM() }, /* @__PURE__ */ React.createElement("label", { className: "textfield__label", htmlFor: this.props.id, id: "".concat(this.props.id, "-label") }, this.props.employee && /* @__PURE__ */ React.createElement(Icon, { modifiers: "#support", className: "textfield__label-icon" }), this.getLabel()), this.props.helpMessage && /* @__PURE__ */ React.createElement(FormHelper, { text: this.props.helpMessage, type: "help", id: "".concat(this.props.id, "-help-message"), className: "textfield__form-helper" }), this.props.errorMessage && /* @__PURE__ */ React.createElement(FormHelper, { text: this.props.errorMessage, type: "error", id: "".concat(this.props.id, "-error-message"), className: "textfield__form-helper" }), this.props.successMessage && /* @__PURE__ */ React.createElement(FormHelper, { text: this.props.successMessage, type: "valid", id: "".concat(this.props.id, "-success-message"), className: "textfield__form-helper" }), this.props.employeeMessage && /* @__PURE__ */ React.createElement(FormHelper, { text: this.props.employeeMessage, type: "employee", id: "".concat(this.props.id, "-employee-message"), className: "textfield__form-helper" }), /* @__PURE__ */ React.createElement("div", { className: "textfield__inner" }, this.props.contentRenderer ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: "textfield__inner-input" }, this.getInner()), /* @__PURE__ */ React.createElement("div", { className: "textfield__inner-content" }, this.props.contentRenderer())) : this.getInner()));
  }
};
_TextFieldInner.defaultProps = {
  type: "text"
};
let TextFieldInner = _TextFieldInner;
export {
  TextField
};
